import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { VisionboardTutorialComponent } from './visionboard-tutorial/visionboard-tutorial.component';
import { VisionboardEditorComponent } from './visionboard-editor/visionboard-editor.component';
import { IkigaiComponent } from './ikigai/ikigai.component';
import { VisionboardService } from '../../../core/services/visionboard.service';
import { AuthService } from '../../../core/services/auth.service';
import { Visionboard, VisionboardImage } from '../../../core/models/visionboard.interface';
import { BaseComponent } from '../../../core/shared/base.component';
import { ConfigurationService } from '../../../core/services/configuration.service';
import { LoaderService } from '../../../core/shared/services/loader.service';

@Component({
  selector: 'app-herramientas',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    VisionboardTutorialComponent,
    VisionboardEditorComponent,
    IkigaiComponent
  ],
  templateUrl: './herramientas.component.html',
  styleUrls: ['./herramientas.component.scss']
})
export class HerramientasComponent extends BaseComponent implements OnInit {

  private loader = inject(LoaderService);
  private authService: AuthService = inject(AuthService);
  private configService = inject(ConfigurationService);
  private visionboardService = inject(VisionboardService);
  private router = inject(Router);

  activeTab: string = 'visionboard';
  showVisionboardTutorial: boolean = false;

  userVisionboards: Visionboard[] = [];
  userId: string | undefined;
  isLoadingVisionboards: boolean = false;

  currentEditorVisionboardId: string | null = null;
  visionboardImages: VisionboardImage[] = [];
  showingEditorForEmptyVisionboard: boolean = false;

  showVisionboardVideo: boolean = false;
  videoIsMuted: boolean = true;
  currentVideoId: string | null = null;


  ngOnInit(): void {
    this.currentEditorVisionboardId = null;

    const authUser = this.authService.getAuthUser();
    if (authUser && authUser.id) {
      this.userId = authUser.id;
      this.loadUserVisionboards();
    } else {
      console.error('HerramientasComponent: Usuario no autenticado.');
      return;
    }
  }

  loadUserVisionboards(): void {
    if (!this.userId) return;
    this.isLoadingVisionboards = true;
    this.visionboardService.getUserVisionboards(this.userId).subscribe({
      next: (response) => {
        this.userVisionboards = response.data || [];
        // Cargar las imágenes también
        if (this.userId) {
          this.loadUserImages();
        }
        this.isLoadingVisionboards = false;
      },
      error: (err) => {
        console.error('Error cargando visionboards del usuario:', err);
        this.isLoadingVisionboards = false;
      }
    });
  }

  loadUserImages(): void {
    if (!this.userId) return;
    this.authService.findUserById(this.userId).subscribe({
      next: (response) => {
        if (response.data && response.data.visionboardImages) {
          this.visionboardImages = response.data.visionboardImages;
        }
      },
      error: (err) => {
        console.error('Error cargando imágenes del usuario:', err);
      }
    });
  }

  setActiveTab(tabName: string): void {
    // No permitir activar la pestaña 'financiera'
    if (tabName === 'financiera') {
      return;
    }

    this.activeTab = tabName;
    if (tabName === 'visionboard' && this.userId) {
      this.loadUserVisionboards();
    }
  }

  closeTutorial(): void {
    this.showVisionboardTutorial = false;
  }

  finishTutorial(): void {
    this.showVisionboardTutorial = false;

    // Guardar en localStorage que el tutorial se ha completado
    localStorage.setItem('visionboardTutorialCompleted', 'true');

    // Verificar que se haya guardado correctamente
    const savedValue = localStorage.getItem('visionboardTutorialCompleted');
    console.log('Tutorial completado. Valor guardado en localStorage:', savedValue);

    // Si no se guardó correctamente, intentar de nuevo
    if (savedValue !== 'true') {
      console.warn('El valor no se guardó correctamente en localStorage. Intentando de nuevo...');
      localStorage.setItem('visionboardTutorialCompleted', 'true');
      console.log('Segundo intento, valor en localStorage:', localStorage.getItem('visionboardTutorialCompleted'));
    }
  }

  navigateToEditor(id?: string): void {
    console.log('Navegando al editor con ID:', id);

    // Primero terminar la carga si está en proceso
    if (this.isLoadingVisionboards) {
      setTimeout(() => this.navigateToEditor(id), 500);
      return;
    }

    // Limpiar el editor antes de configurar un nuevo ID
    this.currentEditorVisionboardId = null;

    // Determinar si debe mostrar el tutorial
    const tutorialCompleted = localStorage.getItem('visionboardTutorialCompleted') === 'true';
    console.log('Estado localStorage - Tutorial completado:', tutorialCompleted);
    console.log('Valor exacto en localStorage:', localStorage.getItem('visionboardTutorialCompleted'));

    if (!tutorialCompleted) this.showVisioBoardVideo();

    // Siempre mostrar el tutorial para edición/creación si no ha sido completado antes
    // this.showVisionboardTutorial = !tutorialCompleted;

    // Establecer bandera para indicar si es un Vision Board vacío
    const hasImages = this.visionboardImages && this.visionboardImages.length > 0;
    this.showingEditorForEmptyVisionboard = !hasImages;
    console.log('Info de diagnóstico:');
    console.log('- ¿Mostrar tutorial?:', this.showVisionboardTutorial);
    console.log('- ¿Usuario tiene imágenes?:', hasImages, '(Total:', this.visionboardImages.length, ')');
    console.log('- ¿Es un Vision Board vacío?:', this.showingEditorForEmptyVisionboard);

    // Usar setTimeout para garantizar que el cambio de null sea detectado
    setTimeout(() => {
      // Si tenemos un ID específico, lo usamos; si no, usamos 'new' para indicar creación de nuevo
      this.currentEditorVisionboardId = id || 'new';
      console.log('ID del editor establecido a:', this.currentEditorVisionboardId);
    }, 100);
  }

  onVisionboardSaved(): void {
    // Limpiar el editor actual
    this.currentEditorVisionboardId = null;
    this.showVisionboardTutorial = false;
    this.showingEditorForEmptyVisionboard = false;

    // Recargar primero las imágenes y luego los visionboards
    if (this.userId) {
      this.authService.findUserById(this.userId).subscribe({
        next: (response) => {
          if (response.data && response.data.visionboardImages) {
            this.visionboardImages = response.data.visionboardImages;
          }
          // Después de cargar las imágenes, cargamos los visionboards
          this.loadUserVisionboards();
        },
        error: (err) => {
          console.error('Error recargando imágenes después de guardar:', err);
          // Aún así, intentamos cargar los visionboards
          this.loadUserVisionboards();
        }
      });
    } else {
      this.loadUserVisionboards();
    }
  }

  // Modificada para aceptar string | undefined
  getPreviewForListItem(imageId: string | undefined): string | null {
    if (!imageId) return null;
    const image = this.visionboardImages.find(img => img.id === imageId);
    return image ? image.imageData : null;
  }

  /**
   * Determina la clase CSS para cada item del collage según su posición
   * @param index Índice de la imagen en el array
   * @param totalImages Total de imágenes disponibles
   * @returns Clase CSS correspondiente al tamaño del item
   */
  getCollageItemClass(index: number, totalImages: number): string {
    // Patrón para crear un diseño de collage visualmente interesante
    if (totalImages <= 1) {
      return 'vb-collage-item--large'; // Si solo hay una imagen, ocupará un espacio grande
    }

    // Patrones para distribución de tamaños en el collage
    if (index === 0) {
      return 'vb-collage-item--large'; // Primera imagen siempre grande
    } else if (index % 5 === 1) {
      return 'vb-collage-item--tall'; // Cada 5 elementos, uno alto
    } else if (index % 7 === 3) {
      return 'vb-collage-item--wide'; // Cada 7 elementos, uno ancho
    } else {
      return ''; // Tamaño normal
    }
  }

  /**
   * Muestra el modal con el video de prueba de autoconocimiento
   */
  showVisioBoardVideo(): void {
    this.loader.show();
    this.configService.getEmbeddedVideos().subscribe({
      next: (response: any) => {
        this.loader.hide();
        if (response?.data && response.data.length > 0) {
          const videoData = response.data.find((video: any) => video.type === 'video_modal_visionboard');

          if (videoData && videoData.url) {
            // Obtener el ID del video
            const videoId = this.extractYoutubeVideoId(videoData.url);
            if (videoId) {
              this.currentVideoId = videoId;
              this.showVideoModal(videoId);
            } else {
              // this.showDefaultAutoconocimientoModal();
            }
          } else {
            // Si no hay video, mostrar el modal con texto
            // this.showDefaultAutoconocimientoModal();
          }
        } else {
          // Si no hay videos configurados, mostrar el modal con texto
          // this.showDefaultAutoconocimientoModal();
        }
      },
      error: (error: any) => {
        console.error('Error al obtener videos embebidos:', error);
        this.loader.hide();
        // En caso de error, mostrar el modal con texto
        // this.showDefaultAutoconocimientoModal();
      }
    });
  }


  /**
   * Muestra el modal con el video y controles de sonido
   */
  showVideoModal(videoId: string): void {
    // Crear estilos para el video y el botón de sonido
    const styles = `
      <style>
        .results-video-container {
          width: 100%;
          display: flex;
          justify-content: center;
        }
        .results-video-wrapper {
          width: 350px;
          height: 350px;
          position: relative;
          overflow: hidden;
          background-color: black;
          border-radius: 0;
        }
        .results-video-iframe {
          position: absolute;
          top: 0%;
          left: 50%;
          width: 100%;
          height: 100%;
          transform: translateX(-50%);
          border: none;
          pointer-events: none;
          object-fit: cover;
        }
        .sound-toggle-btn {
          position: absolute;
          bottom: 55px;
          right: 10px;
          background-color: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100;
          transition: all 0.2s ease;
        }
        .sound-toggle-btn:hover {
          background-color: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }
        .fullscreen-btn {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(0,0,0,0.6);
          border: none;
          border-radius: 50%;
          padding: 8px;
          cursor: pointer;
        }
      </style>
    `;

    // Crear la URL del video con el parámetro mute
    const videoSrc = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&rel=0&showinfo=0&modestbranding=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&enablejsapi=0&origin=${window.location.origin}&hl=en&cc=0&playsinline=1&autohide=1&color=white&start=1&theme=dark&vq=hd1080&showsearch=0&ecver=2&playerapiid=ytplayer&wmode=transparent&annotation=0`;

    // Crear el HTML del video con el botón de sonido
    const videoHtml = `
      ${styles}
      <div class="results-video-container">
        <div class="results-video-wrapper">
          <iframe 
            id="modal-video-frame"
            class="results-video-iframe"
            src="${videoSrc}" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen>
          </iframe>
          <button 
            id="video-sound-toggle" 
            class="sound-toggle-btn" 
            title="Activar sonido">
            <!-- Icono de silencio -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
              <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white" opacity="0.5"/>
              <line x1="23" y1="3" x2="3" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <button id="video-fullscreen-toggle" class="fullscreen-btn" title="Pantalla completa">
            <!-- Icono pantalla completa -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H7v5zm10 4h2v3h-3v2h5v-5h-4zm-3-9v2h3v3h2V5h-5z"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Mostrar el modal con video
    this.info(videoHtml,
      "Crea tu Visionboard",
      "Empezar ahora",
      false,
      `<a id="ver-tutorial-link" role="button" style="font-size: 14px; color: #000000; cursor: pointer;">Ver tutorial</a>`,
      this.showTutorial.bind(this));

    // Agregar el evento al botón de sonido después de que el modal se haya mostrado
    setTimeout(() => {
      const soundToggleBtn = document.getElementById('video-sound-toggle');
      if (soundToggleBtn) {
        soundToggleBtn.addEventListener('click', () => this.toggleVideoSound());
      }
      const fullscreenToggleBtn = document.getElementById('video-fullscreen-toggle');
      if (fullscreenToggleBtn) {
        fullscreenToggleBtn.addEventListener('click', () => this.toggleVideoFulscreen());
      }
    }, 100);
  }

  /**
   * Activa o desactiva el sonido del video
   */
  toggleVideoSound(): void {
    this.videoIsMuted = !this.videoIsMuted;

    if (!this.currentVideoId) return;

    const videoFrame = document.getElementById('modal-video-frame') as HTMLIFrameElement;
    const soundToggleBtn = document.getElementById('video-sound-toggle');

    if (videoFrame) {
      // Crear nueva URL con el parámetro mute actualizado
      const newSrc = `https://www.youtube-nocookie.com/embed/${this.currentVideoId}?autoplay=1&mute=${this.videoIsMuted ? '1' : '0'}&controls=0&loop=1&playlist=${this.currentVideoId}&rel=0&showinfo=0&modestbranding=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&enablejsapi=0&origin=${window.location.origin}&hl=en&cc=0&playsinline=1&autohide=1&color=white&start=1&theme=dark&vq=hd1080&showsearch=0&ecver=2&playerapiid=ytplayer&wmode=transparent&annotation=0`;

      // Actualizar el iframe con la nueva URL
      videoFrame.src = newSrc;
    }

    // Actualizar el botón con el icono correspondiente
    if (soundToggleBtn) {
      soundToggleBtn.innerHTML = this.videoIsMuted ?
        // Icono de silencio
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
          <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white" opacity="0.5"/>
          <line x1="23" y1="3" x2="3" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>` :
        // Icono de sonido activo
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
          <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white"/>
        </svg>`;

      soundToggleBtn.setAttribute('title', this.videoIsMuted ? 'Activar sonido' : 'Silenciar');
    }
  }

  toggleVideoFulscreen(): void{
    const videoFrame = document.getElementById('modal-video-frame') as HTMLIFrameElement;
    const wrapper = videoFrame?.parentElement;

    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    } 
    if (wrapper?.requestFullscreen) {
      wrapper.requestFullscreen();
    } else if ((wrapper as any).webkitRequestFullscreen) {
      (wrapper as any).webkitRequestFullscreen();
    } else if ((wrapper as any).mozRequestFullScreen) {
      (wrapper as any).mozRequestFullScreen();
    } else if ((wrapper as any).msRequestFullscreen) {
      (wrapper as any).msRequestFullscreen();
    } else {
      console.warn('Fullscreen API no soportada');
    }
  }

  /**
   * Helper para extraer el ID de un video de YouTube
   */
  private extractYoutubeVideoId(url: string): string | null {
    return this.getYoutubeVideoId(url);
  }

  showTutorial() {
    this.showVisionboardTutorial = true;
  }

}